import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FaTimes, FaSearch, FaBell, FaChevronDown, FaCog,
  FaSignOutAlt, FaEnvelope, FaCheckCircle, FaExclamationCircle,
  FaFileAlt, FaHandshake, FaCheck, FaTrash,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { API_URL, resolveAvatarUrl } from '../config/api';

const SB_OPEN   = 252;
const SB_CLOSED = 74;
const SB_GAP    = 12;

// Meme cle que Settings.jsx pour partager l'URL resolue
const AVATAR_LS_KEY = 'admin_avatar_url';

const TYPE_CFG = {
  devis:      { icon: <FaFileAlt />,          color: '#fd7e14' },
  contact:    { icon: <FaEnvelope />,          color: '#0d6efd' },
  inscription:{ icon: <FaCheckCircle />,       color: '#198754' },
  partenariat:{ icon: <FaHandshake />,         color: '#6f42c1' },
  alerte:     { icon: <FaExclamationCircle />, color: '#dc3545' },
  systeme:    { icon: <FaCog />,               color: '#20c997' },
};
const getTypeCfg = type => TYPE_CFG[type] || { icon: <FaBell />, color: '#fd7e14' };

const fmtRelTime = d => {
  const diff = Math.floor((Date.now() - new Date(d)) / 60000);
  if (diff < 1)    return "À l'instant";
  if (diff < 60)   return `Il y a ${diff} min`;
  if (diff < 1440) return `Il y a ${Math.floor(diff / 60)}h`;
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

const resolveAvatar = (avatar) => {
  if (!avatar) return null;
  if (avatar.startsWith('blob:'))  return avatar;
  if (avatar.startsWith('http'))   return avatar;
  if (avatar.startsWith('/'))      return resolveAvatarUrl(avatar);
  return resolveAvatarUrl(avatar);
};

const Navbar = ({ sidebarOpen, setSidebarOpen, pageTitle = 'Dashboard' }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user, logout } = useAuth();
  const isDark = theme === 'dark';

  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu,   setShowProfileMenu]   = useState(false);
  const [searchQuery,       setSearchQuery]        = useState('');
  const [searchFocused,     setSearchFocused]      = useState(false);
  const [isLoggingOut,      setIsLoggingOut]       = useState(false);

  const notificationsRef = useRef(null);
  const profileMenuRef   = useRef(null);

  const [recentNotifs, setRecentNotifs] = useState([]);
  const [unreadCount,  setUnreadCount]  = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);

  const token      = localStorage.getItem('admin_token') || '';
  const headers    = { Authorization: `Bearer ${token}` };

  const displayName  = user?.nom_complet || user?.email || 'Admin';
  const displayEmail = user?.email || '';
  const roleLabel    = user?.role === 'super_admin' ? 'Super Administrateur'
                     : user?.role === 'admin'        ? 'Administrateur' : 'Utilisateur';

  const getInitials = name =>
    name.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);

  // FIX : Initialiser avatarUrl depuis la cle dediee en priorite
  // Cette cle contient l'URL deja resolue, donc pas de transformation necessaire
  const [avatarUrl, setAvatarUrl] = useState(() => {
    // Priorite 1 : cle dediee (URL resolue, posee par Settings apres upload)
    const cached = localStorage.getItem(AVATAR_LS_KEY);
    if (cached) return cached;
    // Priorite 2 : user.avatar (chemin brut, a resoudre)
    return resolveAvatar(user?.avatar);
  });

  useEffect(() => {
    if (!token) return;

    // Ecouter l'event custom emis par Settings apres upload
    // detail contient deja l'URL resolue
    const onAvatarUpdate = (e) => {
      const newUrl = e.detail; // peut etre null (suppression)
      setAvatarUrl(newUrl);
      if (newUrl) {
        localStorage.setItem(AVATAR_LS_KEY, newUrl);
      } else {
        localStorage.removeItem(AVATAR_LS_KEY);
      }
    };
    window.addEventListener('avatar-updated', onAvatarUpdate);

    // Ecouter les changements de localStorage depuis d'autres onglets
    const onStorage = (e) => {
      if (e.key === AVATAR_LS_KEY) {
        setAvatarUrl(e.newValue || null);
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('avatar-updated', onAvatarUpdate);
      window.removeEventListener('storage', onStorage);
    };
  }, [token]);

  // Fermer dropdowns au clic exterieur
  useEffect(() => {
    const handler = e => {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) setShowNotifications(false);
      if (profileMenuRef.current   && !profileMenuRef.current.contains(e.target))   setShowProfileMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Notifications
  const fetchRecent = useCallback(async () => {
    if (notifLoading) return;
    setNotifLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/notifications/recent`, { headers });
      setRecentNotifs(data.data || []);
      setUnreadCount(data.unread_count || 0);
    } catch {}
    finally { setNotifLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/admin/notifications/recent`, { headers })
      .then(({ data }) => setUnreadCount(data.unread_count || 0))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleToggleNotifications = () => {
    const next = !showNotifications;
    setShowNotifications(next);
    setShowProfileMenu(false);
    if (next) fetchRecent();
  };

  const handleMarkRead = async (e, id) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API_URL}/admin/notifications/${id}/read`, {}, { headers });
      setRecentNotifs(p => p.map(n => n.id === id ? { ...n, lu: true } : n));
      setUnreadCount(p => Math.max(0, p - 1));
    } catch {}
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/admin/notifications/${id}`, { headers });
      const notif = recentNotifs.find(n => n.id === id);
      setRecentNotifs(p => p.filter(n => n.id !== id));
      if (notif && !notif.lu) setUnreadCount(p => Math.max(0, p - 1));
    } catch {}
  };

  const handleReadAll = async () => {
    try {
      await axios.post(`${API_URL}/admin/notifications/read-all`, {}, { headers });
      setRecentNotifs(p => p.map(n => ({ ...n, lu: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try { await axios.post(`${API_URL}/logout`, {}, { headers }); } catch {}
    finally {
      localStorage.removeItem('welcome_shown');
      localStorage.removeItem(AVATAR_LS_KEY); // FIX : nettoyer aussi la cle avatar
      logout(); // Utiliser la fonction logout du contexte
      toast.info('Vous avez été déconnecté.', {
        position: 'top-right', autoClose: 2000,
        style: { borderRadius: '12px', fontWeight: '600' },
      });
      setTimeout(() => navigate('/login'), 800);
    }
  };

  const navLeft = `${SB_GAP + (sidebarOpen ? SB_OPEN : SB_CLOSED) + 12}px`;

  const c = isDark ? {
    bg: 'rgba(11,14,24,0.92)', border: 'rgba(255,255,255,0.08)', text: '#c8d4e0',
    textDim: '#5a6b82', title: '#eef2f7', surface: 'rgba(255,255,255,0.05)',
    shadow: '0 8px 32px rgba(0,0,0,0.5)', ddBg: '#0e1422', ddBorder: 'rgba(255,255,255,0.08)',
    ddItem: 'rgba(255,255,255,0.03)', inputBg: 'rgba(255,255,255,0.05)', sep: 'rgba(255,255,255,0.07)',
  } : {
    bg: 'rgba(255,255,255,0.95)', border: 'rgba(0,0,0,0.07)', text: '#4d6070',
    textDim: '#8fa3b5', title: '#1a2535', surface: 'rgba(0,0,0,0.03)',
    shadow: '0 4px 24px rgba(0,0,0,0.08)', ddBg: '#ffffff', ddBorder: 'rgba(0,0,0,0.07)',
    ddItem: 'rgba(0,0,0,0.02)', inputBg: 'rgba(0,0,0,0.04)', sep: 'rgba(0,0,0,0.07)',
  };

  // FIX : Avatar utilise avatarUrl (state dedie, persistent)
  const Avatar = ({ size = 30, radius = '50%', fontSize = 11 }) => (
    <div style={{ width: size, height: size, borderRadius: radius, overflow: 'hidden', flexShrink: 0, position: 'relative', background: 'linear-gradient(135deg,#fd7e14,#dc3545)' }}>
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={displayName}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={() => {
            // L'image ne charge plus : nettoyer le cache
            setAvatarUrl(null);
            localStorage.removeItem(AVATAR_LS_KEY);
          }}
        />
      ) : (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          {getInitials(displayName)}
        </div>
      )}
    </div>
  );

  return (
    <>
      <header className="nb" style={{ left: navLeft, background: c.bg, borderColor: c.border, boxShadow: c.shadow }}>
        <div className="nb-container">

          {/* Gauche */}
          <div className="nb-left">
            <div className="nb-page-info">
              <span className="nb-tag">Admin</span>
              <h1 className="nb-title" style={{ color: c.title }}>{pageTitle}</h1>
            </div>
          </div>

          {/* Droite */}
          <div className="nb-right">

            {/* Recherche */}
            <form className="nb-search" onSubmit={e => e.preventDefault()}>
              <div className="nb-search-inner">
                <FaSearch className="nb-search-icon" style={{ color: searchFocused ? '#fd7e14' : c.textDim }} />
                <input
                  type="text" placeholder="Rechercher..."
                  value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)}
                  className="nb-search-input"
                  style={{ background: c.inputBg, borderColor: searchFocused ? '#fd7e14' : c.border, color: c.text, boxShadow: searchFocused ? '0 0 0 3px rgba(253,126,20,0.12)' : 'none' }}
                />
                {searchQuery && <button type="button" className="nb-clear" onClick={() => setSearchQuery('')}><FaTimes /></button>}
              </div>
            </form>

            <div className="nb-sep" style={{ background: c.sep }} />

            {/* Cloche */}
            <div className="nb-wrap" ref={notificationsRef}>
              <button
                className="nb-icon-btn"
                onClick={handleToggleNotifications}
                style={{ background: showNotifications ? 'rgba(253,126,20,0.1)' : c.surface, borderColor: showNotifications ? 'rgba(253,126,20,0.3)' : c.border, color: showNotifications ? '#fd7e14' : c.textDim }}
              >
                <FaBell />
                {unreadCount > 0 && <span className="nb-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
              </button>

              {showNotifications && (
                <div className="nb-dd" style={{ background: c.ddBg, borderColor: c.ddBorder }}>
                  <div className="nb-dd-head" style={{ borderBottomColor: c.ddBorder }}>
                    <span className="nb-dd-title" style={{ color: c.title }}>Notifications</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {unreadCount > 0 && <span className="nb-dd-count">{unreadCount} nouvelle{unreadCount > 1 ? 's' : ''}</span>}
                      {unreadCount > 0 && (
                        <button onClick={handleReadAll} title="Tout marquer comme lu"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#198754', fontSize: 11, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                          <FaCheck style={{ marginRight: 3, fontSize: 9 }} />Tout lire
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="nb-notif-list">
                    {notifLoading ? (
                      [...Array(3)].map((_, i) => (
                        <div key={i} style={{ padding: '13px 18px', display: 'flex', gap: 12 }}>
                          <div style={{ width: 38, height: 38, borderRadius: 10, background: isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0', flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ height: 12, borderRadius: 6, background: isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0', marginBottom: 7, width: '70%' }} />
                            <div style={{ height: 10, borderRadius: 5, background: isDark ? 'rgba(255,255,255,0.04)' : '#f5f5f5', width: '90%' }} />
                          </div>
                        </div>
                      ))
                    ) : recentNotifs.length === 0 ? (
                      <div style={{ padding: '32px 20px', textAlign: 'center' }}>
                        <FaBell style={{ fontSize: 28, color: c.textDim, opacity: 0.3, display: 'block', margin: '0 auto 10px' }} />
                        <p style={{ fontSize: 13, color: c.textDim, margin: 0 }}>Aucune notification</p>
                      </div>
                    ) : recentNotifs.map(n => {
                      const cfg = getTypeCfg(n.type);
                      return (
                        <div key={n.id} className="nb-notif-item"
                          style={{ background: !n.lu ? `${cfg.color}09` : c.ddItem, borderBottom: `1px solid ${c.ddBorder}`, cursor: 'pointer' }}
                          onClick={() => { navigate('/notifications'); setShowNotifications(false); }}>
                          <div className="nb-notif-icon" style={{ background: `${cfg.color}18`, color: cfg.color }}>{cfg.icon}</div>
                          <div className="nb-notif-body" style={{ flex: 1, minWidth: 0 }}>
                            <p className="nb-notif-title" style={{ color: c.title, fontWeight: !n.lu ? 700 : 500 }}>{n.titre}</p>
                            <p className="nb-notif-msg"   style={{ color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>{n.message}</p>
                            <span className="nb-notif-time" style={{ color: c.textDim }}>{fmtRelTime(n.date)}</span>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                            {!n.lu && (
                              <button title="Marquer comme lu" onClick={e => handleMarkRead(e, n.id)}
                                style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid rgba(25,135,84,.25)', background: 'rgba(25,135,84,.08)', color: '#198754', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, cursor: 'pointer' }}>
                                <FaCheck />
                              </button>
                            )}
                            <button title="Supprimer" onClick={e => handleDelete(e, n.id)}
                              style={{ width: 26, height: 26, borderRadius: 6, border: `1px solid ${c.ddBorder}`, background: 'transparent', color: c.textDim, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, cursor: 'pointer' }}
                              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,53,69,.1)'; e.currentTarget.style.color = '#dc3545'; e.currentTarget.style.borderColor = 'rgba(220,53,69,.25)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = c.textDim; e.currentTarget.style.borderColor = c.ddBorder; }}>
                              <FaTrash />
                            </button>
                          </div>
                          {!n.lu && <span className="nb-unread-dot" style={{ alignSelf: 'flex-start', marginTop: 6 }} />}
                        </div>
                      );
                    })}
                  </div>

                  <div className="nb-dd-foot" style={{ borderTopColor: c.ddBorder }}>
                    <button className="nb-view-all" onClick={() => { navigate('/notifications'); setShowNotifications(false); }}>
                      Voir toutes les notifications
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Profil */}
            <div className="nb-wrap" ref={profileMenuRef}>
              <button
                className="nb-profile-btn"
                onClick={() => { setShowProfileMenu(!showProfileMenu); setShowNotifications(false); }}
                style={{ background: c.surface, borderColor: showProfileMenu ? 'rgba(253,126,20,0.3)' : c.border }}
              >
                <Avatar size={30} radius="50%" fontSize={11} />
                <div className="nb-profile-info">
                  <span className="nb-profile-name" style={{ color: c.title }}>{displayName}</span>
                  <span className="nb-profile-role"  style={{ color: c.textDim }}>{roleLabel}</span>
                </div>
                <FaChevronDown className="nb-chevron" style={{ color: c.textDim, transform: showProfileMenu ? 'rotate(180deg)' : 'rotate(0deg)' }} />
              </button>

              {showProfileMenu && (
                <div className="nb-dd nb-dd--profile" style={{ background: c.ddBg, borderColor: c.ddBorder }}>
                  <div className="nb-dd-profile-head" style={{ borderBottomColor: c.ddBorder }}>
                    <div className="nb-avatar-lg">
                      <Avatar size={60} radius="14px" fontSize={20} />
                    </div>
                    <p className="nb-dd-name"  style={{ color: c.title }}>{displayName}</p>
                    <p className="nb-dd-email" style={{ color: c.textDim }}>{displayEmail}</p>
                    <span className="nb-role-badge">{roleLabel}</span>
                  </div>

                  <div className="nb-dd-menu">
                    <button
                      className="nb-menu-item"
                      style={{ color: c.text }}
                      onClick={() => { setShowProfileMenu(false); navigate('/settings'); }}
                    >
                      <span className="nb-menu-icon" style={{ background: c.surface, color: c.textDim }}><FaCog /></span>
                      Paramètres
                    </button>
                  </div>

                  <div className="nb-dd-foot nb-dd-foot--profile" style={{ borderTopColor: c.ddBorder }}>
                    <button className="nb-logout-btn" onClick={handleLogout} disabled={isLoggingOut}>
                      {isLoggingOut ? (
                        <><svg className="nb-spinner" viewBox="0 0 24 24" fill="none" width="15" height="15"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3"/><path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/></svg>Déconnexion...</>
                      ) : (
                        <><FaSignOutAlt />Déconnexion</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
        .nb{position:fixed;top:12px;right:12px;height:68px;backdrop-filter:blur(20px) saturate(150%);-webkit-backdrop-filter:blur(20px) saturate(150%);border:1px solid;border-radius:16px;z-index:900;transition:left 0.28s cubic-bezier(0.4,0,0.2,1);overflow:visible;font-family:'Plus Jakarta Sans',sans-serif;}
        .nb-container{height:100%;display:flex;align-items:center;justify-content:space-between;padding:0 20px;}
        .nb-left{display:flex;align-items:center;}
        .nb-page-info{display:flex;flex-direction:column;gap:0px;}
        .nb-tag{font-size:10px;font-weight:600;letter-spacing:2px;text-transform:uppercase;color:#fd7e14;opacity:0.9;line-height:1;margin-bottom:1px;}
        .nb-title{font-size:21px;font-weight:700;margin:0;letter-spacing:-0.2px;line-height:1.1;}
        .nb-right{display:flex;align-items:center;gap:8px;}
        .nb-search{display:flex;align-items:center;}
        .nb-search-inner{position:relative;display:flex;align-items:center;}
        .nb-search-icon{position:absolute;left:13px;font-size:13px;pointer-events:none;z-index:1;transition:color 0.2s;}
        .nb-search-input{width:220px;height:40px;padding:0 36px 0 38px;border:1.5px solid;border-radius:11px;font-size:14.5px;font-family:'Plus Jakarta Sans',sans-serif;font-weight:400;transition:all 0.2s;outline:none;}
        .nb-search-input::placeholder{color:#8fa3b5;}
        .nb-clear{position:absolute;right:10px;width:20px;height:20px;border-radius:50%;border:none;background:rgba(128,128,128,0.2);color:#8fa3b5;display:flex;align-items:center;justify-content:center;font-size:9px;cursor:pointer;padding:0;transition:all 0.18s;}
        .nb-clear:hover{background:#dc3545;color:white;}
        .nb-sep{width:1px;height:26px;flex-shrink:0;}
        .nb-icon-btn{width:40px;height:40px;display:flex;align-items:center;justify-content:center;border:1.5px solid;border-radius:11px;font-size:16px;cursor:pointer;transition:all 0.2s;position:relative;padding:0;}
        .nb-icon-btn:hover{background:rgba(253,126,20,0.1)!important;border-color:rgba(253,126,20,0.3)!important;color:#fd7e14!important;}
        .nb-badge{position:absolute;top:-6px;right:-6px;min-width:18px;height:18px;padding:0 5px;background:#dc3545;color:white;font-size:10px;font-weight:700;font-family:'Plus Jakarta Sans',sans-serif;border-radius:9px;display:flex;align-items:center;justify-content:center;animation:nb-pulse 2.5s ease infinite;}
        @keyframes nb-pulse{0%,100%{box-shadow:0 0 0 0 rgba(220,53,69,0.4);}50%{box-shadow:0 0 0 5px rgba(220,53,69,0);}}
        .nb-profile-btn{display:flex;align-items:center;gap:9px;padding:4px 10px 4px 4px;border:1.5px solid;border-radius:40px;cursor:pointer;transition:all 0.2s;height:40px;}
        .nb-profile-btn:hover{background:rgba(253,126,20,0.08)!important;border-color:rgba(253,126,20,0.3)!important;}
        .nb-profile-info{display:flex;flex-direction:column;gap:0;}
        .nb-profile-name{font-size:14px;font-weight:600;font-family:'Plus Jakarta Sans',sans-serif;white-space:nowrap;line-height:1.2;}
        .nb-profile-role{font-size:11px;font-family:'Plus Jakarta Sans',sans-serif;white-space:nowrap;font-weight:400;}
        .nb-chevron{font-size:10px;transition:transform 0.22s ease;flex-shrink:0;}
        .nb-wrap{position:relative;}
        .nb-dd{position:absolute;top:calc(100% + 10px);right:0;width:400px;border:1px solid;border-radius:16px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,0.18);animation:nb-drop 0.2s ease;z-index:2000;}
        @keyframes nb-drop{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
        .nb-dd-head{display:flex;justify-content:space-between;align-items:center;padding:14px 18px;border-bottom:1px solid;}
        .nb-dd-title{font-family:'Plus Jakarta Sans',sans-serif;font-size:16px;font-weight:700;}
        .nb-dd-count{font-size:12px;font-weight:600;color:#fd7e14;background:rgba(253,126,20,0.1);border:1px solid rgba(253,126,20,0.2);padding:3px 10px;border-radius:20px;}
        .nb-notif-list{max-height:320px;overflow-y:auto;scrollbar-width:none;}
        .nb-notif-list::-webkit-scrollbar{display:none;}
        .nb-notif-item{display:flex;align-items:flex-start;gap:11px;padding:12px 16px;transition:background 0.15s;}
        .nb-notif-item:hover{background:rgba(253,126,20,0.04)!important;}
        .nb-notif-icon{width:36px;height:36px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0;}
        .nb-notif-title{font-size:13.5px;font-weight:600;font-family:'Plus Jakarta Sans',sans-serif;margin:0 0 2px;}
        .nb-notif-msg{font-size:12.5px;font-family:'Plus Jakarta Sans',sans-serif;margin:0 0 3px;}
        .nb-notif-time{font-size:11px;font-weight:500;font-family:'Plus Jakarta Sans',sans-serif;}
        .nb-unread-dot{width:8px;height:8px;background:#fd7e14;border-radius:50%;flex-shrink:0;box-shadow:0 0 6px rgba(253,126,20,0.5);}
        .nb-dd-foot{padding:12px 18px;border-top:1px solid;}
        .nb-view-all{width:100%;padding:10px;background:transparent;border:none;color:#fd7e14;font-size:14px;font-weight:600;font-family:'Plus Jakarta Sans',sans-serif;cursor:pointer;border-radius:9px;transition:background 0.18s;}
        .nb-view-all:hover{background:rgba(253,126,20,0.08);}
        .nb-dd--profile{width:260px;}
        .nb-dd-profile-head{padding:22px 18px 16px;text-align:center;border-bottom:1px solid;display:flex;flex-direction:column;align-items:center;gap:8px;}
        .nb-avatar-lg{border:3px solid rgba(253,126,20,0.3);border-radius:17px;box-shadow:0 4px 14px rgba(253,126,20,0.15);overflow:hidden;flex-shrink:0;}
        .nb-dd-name{font-size:16px;font-weight:700;font-family:'Plus Jakarta Sans',sans-serif;margin:0;}
        .nb-dd-email{font-size:13px;font-family:'Plus Jakarta Sans',sans-serif;margin:0;font-weight:400;}
        .nb-role-badge{display:inline-block;font-size:11px;font-weight:600;color:#198754;background:rgba(25,135,84,0.1);border:1px solid rgba(25,135,84,0.2);padding:3px 10px;border-radius:20px;font-family:'Plus Jakarta Sans',sans-serif;}
        .nb-dd-menu{padding:8px;}
        .nb-menu-item{width:100%;display:flex;align-items:center;gap:11px;padding:10px 12px;background:transparent;border:none;border-radius:9px;font-size:15px;font-weight:500;font-family:'Plus Jakarta Sans',sans-serif;cursor:pointer;transition:all 0.18s;text-align:left;}
        .nb-menu-item:hover{background:rgba(253,126,20,0.08);color:#fd7e14!important;transform:translateX(3px);}
        .nb-menu-item:hover .nb-menu-icon{color:#fd7e14!important;}
        .nb-menu-icon{width:30px;height:30px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:13px;flex-shrink:0;transition:all 0.18s;}
        .nb-dd-foot--profile{padding:8px;}
        .nb-logout-btn{width:100%;display:flex;align-items:center;justify-content:center;gap:9px;padding:11px;background:linear-gradient(135deg,#dc3545 0%,#c82333 100%);border:none;border-radius:10px;font-size:15px;font-weight:600;font-family:'Plus Jakarta Sans',sans-serif;color:white;cursor:pointer;transition:all 0.2s;box-shadow:0 4px 14px rgba(220,53,69,0.25);}
        .nb-logout-btn:hover{transform:translateY(-1px);box-shadow:0 6px 20px rgba(220,53,69,0.35);}
        .nb-logout-btn:disabled{opacity:0.75;cursor:not-allowed;transform:none;}
        .nb-spinner{animation:nb-spin 0.8s linear infinite;flex-shrink:0;}
        @keyframes nb-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @media(max-width:1024px){.nb{left:12px!important;}.nb-search-input{width:160px;}.nb-profile-info{display:none;}.nb-chevron{display:none;}.nb-profile-btn{padding:4px;border-radius:50%;}}
        @media(max-width:600px){.nb{left:8px!important;right:8px;top:8px;height:60px;}.nb-search{display:none;}.nb-sep{display:none;}.nb-title{font-size:18px;}.nb-dd{width:calc(100vw - 20px);right:-8px;}}
      `}</style>
    </>
  );
};

export default Navbar;